import { Auth0Provider } from '@auth0/auth0-react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import devExtremeConfig from 'devextreme/core/config';
import DineroFactory from 'dinero.js';
import LogRocket from 'logrocket';
import { Settings } from 'luxon';
import type { ReactElement } from 'react';

import { hasStringValue } from '@core/utils/equality';
import { SlabTheme } from '@web/theme';

import { AppUpdates } from './AppUpdates';
import { ConfirmProvider } from './providers/ConfirmProvider/ConfirmProvider';
import { ToastProvider } from './providers/ToastProvider/ToastProvider';
import { SlabQueryClient } from './QueryClient';
import { SlabRouter } from './Router';
import { SlabAuthProvider } from './SlabAuthProvider';
import { Auth0IndexedDBCache } from './utils/auth0Cache';
import { dropExpectedAuthErrors } from './utils/dropExpectedAuthErrors';
import { migrateAuth0CacheToIndexedDB } from './utils/migrateAuth0Cache';
import { SlabConfig } from './utils/SlabConfig';

declare global {
  interface Window {
    Intercom: any;
  }
}

// Set default Luxon timezone to system time. The backend works exclusively in
// UTC; Yup helpers and generated domain constructors convert back and forth
// between UTC and system time as needed.
Settings.defaultZone = 'system';

// Set DevExtreme config
devExtremeConfig({ licenseKey: SlabConfig.devExtreme.licenseKey });

// Set default Dinero settings
DineroFactory.globalLocale = 'en-US';
DineroFactory.defaultCurrency = 'USD';
DineroFactory.defaultPrecision = 2;

if (SlabConfig.logRocket.appID !== undefined) {
  LogRocket.init(SlabConfig.logRocket.appID, {
    release: SlabConfig.build,
  });
  LogRocket.track('Screen Info', {
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
  });
}

if (hasStringValue(SlabConfig.sentry.dsn)) {
  Sentry.init({
    dsn: SlabConfig.sentry.dsn,
    ignoreErrors: ['Object Not Found Matching Id:'],
    environment: import.meta.env.MODE,
    release: SlabConfig.build,
    sendDefaultPii: true,
    beforeSend: dropExpectedAuthErrors,
  });
}

// Create a single instance of the cache to be reused
const auth0Cache = new Auth0IndexedDBCache();

// Migrate existing Auth0 tokens from localStorage to IndexedDB
// This runs once on app startup to preserve existing sessions
migrateAuth0CacheToIndexedDB().catch((error) => {
  LogRocket.error('Failed to migrate Auth0 cache', error);
});

export const App = (): ReactElement => {
  if (SlabConfig.auth0.domain === undefined || SlabConfig.auth0.clientID === undefined) {
    // TODO: #288 Replace with error page
    return <div>An unknown Slabstack error occurred</div>;
  }

  return (
    <Auth0Provider
      domain={SlabConfig.auth0.domain}
      clientId={SlabConfig.auth0.clientID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: SlabConfig.auth0.audience,
        scope: 'openid profile email offline_access',
      }}
      cache={auth0Cache}
      useRefreshTokens
      useRefreshTokensFallback
    >
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <QueryClientProvider client={SlabQueryClient}>
          <ThemeProvider theme={SlabTheme}>
            <CssBaseline />
            <ConfirmProvider>
              <ToastProvider>
                <AppUpdates />
                <SlabAuthProvider>
                  <SlabRouter />
                </SlabAuthProvider>
              </ToastProvider>
            </ConfirmProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </LocalizationProvider>
    </Auth0Provider>
  );
};
